import { Search } from "lucide-react";
import { useRef, useEffect, useState, useCallback } from "react";
import { cn } from "@repo/utils";
import { Button } from "../button/button.js";
import { Input } from "../inputs/input.js";

type Props = {
  toolbarActions?: React.ReactNode;
  renderColumnFilters?: React.ReactNode;
  hasSearchableColumns: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  isSearchExpanded: boolean;
  onSearchExpandedChange: (expanded: boolean) => void;
};

export const DataTableToolbar = ({
  toolbarActions,
  renderColumnFilters,
  hasSearchableColumns,
  searchTerm,
  onSearchChange,
  isSearchExpanded,
  onSearchExpandedChange,
}: Props) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedSearch = useCallback(
    (value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 300);
    },
    [onSearchChange]
  );

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsFocused(false);
        if (!localSearchTerm) {
          onSearchExpandedChange(false);
        }
      }
    };

    if (isSearchExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isSearchExpanded, localSearchTerm, onSearchExpandedChange]);

  useEffect(() => {
    if (isSearchExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchExpanded]);

  return (
    <div className="flex items-center gap-2">
      {toolbarActions && (
        <div className="flex flex-shrink-0 items-center gap-2">{toolbarActions}</div>
      )}
      {renderColumnFilters && (
        <div className="flex flex-shrink-0 items-center gap-2">{renderColumnFilters}</div>
      )}
      {hasSearchableColumns && (
        <div
          ref={searchRef}
          onClick={() => {
            onSearchExpandedChange(true);
            setIsFocused(true);
          }}
          className={cn(
            "border-gxe-border flex items-center overflow-hidden rounded-lg border bg-white transition-all duration-100 ease-in-out",
            {
              "w-64 gap-2 px-3 py-2": isSearchExpanded,
              "border-gxe-primary border-2": isSearchExpanded && isFocused && !localSearchTerm,
              "border-gxe-primary border-2 !transition-colors !duration-0":
                isSearchExpanded && isFocused && localSearchTerm,
              "border-gxe-border border !transition-colors !duration-0":
                isSearchExpanded && !isFocused,
              "hover:bg-gxe-bg-gray w-10 cursor-pointer justify-center py-2 hover:text-gray-600":
                !isSearchExpanded,
            }
          )}
        >
          <Button
            variant="ghost"
            size="icon_sm"
            onClick={() => onSearchExpandedChange(!isSearchExpanded)}
            className={cn("size-fit flex-shrink-0 text-gray-400", {
              "size-full hover:bg-transparent": !isSearchExpanded,
            })}
          >
            <Search className="h-4 w-4" />
          </Button>
          <Input
            type="text"
            ref={inputRef}
            placeholder="Search"
            value={localSearchTerm}
            onChange={(e) => {
              setLocalSearchTerm(e.target.value);
              debouncedSearch(e.target.value);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={cn(
              "text-medium size-fit rounded-none border-0 bg-transparent p-0 text-sm transition-all duration-300 ease-in-out outline-none placeholder:text-gray-400 focus:ring-0 focus:outline-none",
              {
                "w-full opacity-100": isSearchExpanded,
                "w-0 opacity-0": !isSearchExpanded,
              }
            )}
          />
        </div>
      )}
    </div>
  );
};

DataTableToolbar.displayName = "DataTableToolbar";
